import React, { useCallback, useEffect } from 'react';

import { AccordionItem } from '@radix-ui/react-accordion';
import { type InfiniteData } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useMeasure } from 'react-use';

import { ReactComponent as CloseButton } from '@pkg/assets/icons/v2/close-circle.svg';
import { ReactComponent as SearchIcon } from '@pkg/assets/icons/v2/search.svg';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { FilterAction, FilterContent, FilterHeading, type FilterIndex } from '@pkg/components/Sections/Profile/Filters';
import { DepInput } from '@pkg/components/deprecated';
import { getPagesContent } from '@pkg/hooks';
import { type NftCollectionsSingle, NftFiltersKey, useMediaQuery, useNftFilter } from '@pkg/providers';

import { type GetGroupCollectionsResponse } from '@app/client/common/structs/groups/groups';

import { GroupFilterNoResults } from '../GroupFilterNoResults';

import { CollectionItem } from './CollectionItem';
import './groupFilter.scss';
import { FadeDiv, GroupFilterCollectionsOverride, LoadMoreButtonWrapper } from './style';

export interface CollectionsFilterContentProps extends FilterIndex {
    data: InfiniteData<GetGroupCollectionsResponse> | undefined;
    isError: boolean;
    isLoading: boolean;
    nameToFind: string;
    hasNextPage?: boolean;
    fetchNextPage(): void;
    isFetchingNextPage?: boolean;
    refetch(): void;
    setNameToFind(name: string): void;
    height?: number;
    isPreviousData?: boolean;
    hideMembers?: boolean;
}

const FILTER_KEY = NftFiltersKey.Collections;

export function CollectionsFilterContent({
    data,
    nameToFind,
    value,
    isError,
    isLoading,
    refetch,
    hasNextPage,
    setNameToFind,
    fetchNextPage,
    isPreviousData,
    isFetchingNextPage,
    height = 160,
    hideMembers,
}: CollectionsFilterContentProps) {
    const { t: translate } = useTranslation(['groups']);
    const { ref, inView } = useInView();
    const { tablet } = useMediaQuery();

    const [filters, setFilters] = useNftFilter();

    useEffect(() => {
        if (inView && !isFetchingNextPage && !isLoading) {
            fetchNextPage();
        }
    }, [inView, isFetchingNextPage, isLoading, fetchNextPage]);

    function handleUpdateCollections(collection: NftCollectionsSingle) {
        if (filters.hasSomeValue(FILTER_KEY, collection)) {
            filters.remove(FILTER_KEY, collection);
        } else {
            filters.append(FILTER_KEY, collection);
        }

        setFilters(filters);
    }

    const [headingRef, { height: headingHeight }] = useMeasure<HTMLDivElement>();
    const filterContentHeight = height - headingHeight - 20;

    const content = getPagesContent(data?.pages);

    const onChangeName = useCallback(() => {
        return debounce(() => {
            refetch();
        }, 500);
    }, [refetch]);

    if (isError || isLoading) {
        return null;
    }

    const changeName = (newName: string) => {
        setNameToFind(newName);
        onChangeName();
    };

    const collectionsItems = content?.length
        ? content.map(({ collection_image, collection_name, contract_address, item_count, member_count }) => (
              <FilterAction
                  key={`CollectionsFilterContent-${contract_address}`}
                  pressed={filters.hasSomeValue(FILTER_KEY, contract_address)}
                  onAction={() => handleUpdateCollections(contract_address)}
              >
                  <CollectionItem
                      name={collection_name}
                      avatar={collection_image}
                      assetTotal={item_count}
                      memberTotal={member_count}
                      hideMembers={hideMembers}
                  />
              </FilterAction>
          ))
        : null;

    const resultsMarkup = collectionsItems?.length ? collectionsItems : <GroupFilterNoResults />;

    return (
        <AccordionItem value={value}>
            <div ref={headingRef}>
                <FilterHeading disableUpperCase>{translate('groups_filter_collections_title')}</FilterHeading>
                <DepInput
                    autoFocus={false}
                    isInvalid={false}
                    classes="group_filter_collection_input"
                    placeholder={translate('groups_filter_collections_search')}
                    value={nameToFind}
                    prepend={<SearchIcon fill="#ffffff" />}
                    append={nameToFind ? <CloseButton onClick={() => changeName('')} fill="#ffffff" /> : null}
                    onChange={(e) => changeName(e.target.value)}
                />
            </div>

            <GroupFilterCollectionsOverride>
                <FilterContent height={`${filterContentHeight}px`} noBorder containScroll>
                    <FadeDiv />

                    {resultsMarkup}

                    {hasNextPage ? (
                        <LoadMoreButtonWrapper>
                            <Button
                                ref={ref}
                                fluid
                                loading={isFetchingNextPage}
                                variant="solid"
                                color="translucent"
                                size={tablet ? 'small' : 'medium'}
                                disabled={isPreviousData || !hasNextPage || isFetchingNextPage}
                                onClick={() => fetchNextPage()}
                            >
                                {translate('groups_btn_load_more_collections')}
                            </Button>
                        </LoadMoreButtonWrapper>
                    ) : null}
                </FilterContent>
            </GroupFilterCollectionsOverride>
        </AccordionItem>
    );
}
